import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { msalInstance } from '../config/authConfig';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { isE2EMockAuth, getE2EMockRole, getE2EMockSecret } from '../utils/e2eMockAuth';

const isWizardDebug =
  (import.meta.env.VITE_LOG_LEVEL || '').toLowerCase() === 'debug'

const isRoleDebug = import.meta.env.VITE_ROLE_DEBUG === 'true'
const isApiDebug = import.meta.env.VITE_API_DEBUG === 'true'

function logWizardApi(
  message: string,
  details?: Record<string, any>
): void {
  if (!isWizardDebug) return
  if (details) {
    console.log(`[WIZARD_API] ${message}`, details)
  } else {
    console.log(`[WIZARD_API] ${message}`)
  }
}

// Utility function to extract company IDs from user roles
export function getUserCompanyIds(): string[] {
  if (isE2EMockAuth()) return [];
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      if (isRoleDebug) console.log('[ROLE_DEBUG] No MSAL accounts found');
      return [];
    }

    const account = accounts[0];
    const roles = (account?.idTokenClaims as unknown as { roles?: string[] })?.roles || [];

    if (isRoleDebug) {
      console.log('[ROLE_DEBUG] User roles from token:', roles);
      console.log('[ROLE_DEBUG] Full ID token claims:', account.idTokenClaims);
    }

    if (!Array.isArray(roles)) {
      if (isRoleDebug) console.log('[ROLE_DEBUG] Roles is not an array:', roles);
      return [];
    }

    const companyIds: string[] = [];

    for (const role of roles) {
      if (isRoleDebug) console.log(`[ROLE_DEBUG] Processing role: "${role}"`);

      // Try multiple patterns to extract company ID
      let companyId = null;

      // Pattern 1: {company-id}-user or {company-id}-consultant
      let match = role.match(/^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})-(user|consultant)$/i);
      if (match) {
        companyId = match[1];
        if (isRoleDebug) console.log(`[ROLE_DEBUG] Pattern 1 match: "${companyId}" from role: "${role}"`);
      } else {
        // Pattern 2: Just the company ID (if role is just the UUID)
        match = role.match(/^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i);
        if (match) {
          companyId = match[1];
          if (isRoleDebug) console.log(`[ROLE_DEBUG] Pattern 2 match: "${companyId}" from role: "${role}"`);
        } else {
          // Pattern 3: Company ID with any suffix (more flexible)
          match = role.match(/^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})-/i);
          if (match) {
            companyId = match[1];
            if (isRoleDebug) console.log(`[ROLE_DEBUG] Pattern 3 match: "${companyId}" from role: "${role}"`);
          } else {
            if (isRoleDebug) console.log(`[ROLE_DEBUG] Role "${role}" does not match any company pattern`);
          }
        }
      }

      if (companyId && !companyIds.includes(companyId)) {
        companyIds.push(companyId);
      }
    }

    if (isRoleDebug) console.log('[ROLE_DEBUG] Final company IDs:', companyIds);
    return companyIds;
  } catch (error) {
    console.error('Error extracting company IDs from roles:', error);
    return [];
  }
}

// Utility function to check if user has admin role
export function isUserAdmin(): boolean {
  if (isE2EMockAuth()) return getE2EMockRole() === 'admin';
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      if (isRoleDebug) console.log('[ROLE_DEBUG] No MSAL accounts found for admin check');
      return false;
    }

    const account = accounts[0];
    const roles = (account?.idTokenClaims as unknown as { roles?: string[] })?.roles || [];

    const isAdmin = Array.isArray(roles) && roles.includes('admin');
    if (isRoleDebug) {
      console.log('[ROLE_DEBUG] Is admin:', isAdmin, 'Roles:', roles);
      console.log('[ROLE_DEBUG] Full ID token claims for admin check:', account.idTokenClaims);
    }
    return isAdmin;
  } catch (error) {
    console.error('Error checking admin role:', error);
    return false;
  }
}

class ApiClient {
  private axiosInstance: AxiosInstance;
  private activeRequests = 0;
  private loadingListeners: ((isLoading: boolean) => void)[] = [];

  constructor() {
    const baseURL = getApiBaseUrl();

    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add authentication & tracking
    this.axiosInstance.interceptors.request.use(async (config) => {
      // Track request start
      this.activeRequests++;
      this.notifyLoading(true);

      try {
        // E2E mock auth: send mock headers instead of MSAL token
        if (isE2EMockAuth()) {
          const role = getE2EMockRole()!;
          const secret = getE2EMockSecret();
          config.headers['x-mock-auth-secret'] = secret;
          config.headers['x-mock-user'] = JSON.stringify({
            userId: `e2e-${role}`,
            userName: `E2E ${role.charAt(0).toUpperCase() + role.slice(1)}`,
            roles: [role],
          });
          return config;
        }

        const accounts = msalInstance.getAllAccounts();
        if (isApiDebug) console.log('[API_DEBUG] MSAL accounts found:', accounts.length);

        if (accounts.length > 0) {
          const account = accounts[0];

          // Try to get the ID token first (contains roles)
          let token = account.idToken;

          if (!token) {
            // Fallback to acquiring a new token
            const response = await msalInstance.acquireTokenSilent({
              scopes: ['openid', 'profile'],
              account: account,
            });
            token = response.idToken || response.accessToken;
          }

          if (isApiDebug) {
            console.log('[API_DEBUG] Token acquired:', {
              hasIdToken: !!account.idToken,
              tokenLength: token?.length,
              tokenPreview: token?.substring(0, 50) + '...',
              tokenClaims: account.idTokenClaims,
              roles: account.idTokenClaims?.roles
            });
          }

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            if (isApiDebug) console.log('[API_DEBUG] Authorization header set for URL:', config.url);
          } else {
            if (isApiDebug) console.log('[API_DEBUG] No token available');
          }
        } else {
          if (isApiDebug) console.log('[API_DEBUG] No MSAL accounts found, no token added');
        }
      } catch (error) {
        if (isApiDebug) console.warn('[API_DEBUG] Failed to acquire token silently:', error);
      }
      return config;
    }, (error) => {
      this.activeRequests--;
      this.notifyLoading(this.activeRequests > 0);
      return Promise.reject(error);
    });

    // Response interceptor for error handling & tracking
    this.axiosInstance.interceptors.response.use(
      (response) => {
        this.activeRequests--;
        this.notifyLoading(this.activeRequests > 0);
        return response;
      },
      (error: AxiosError) => {
        this.activeRequests--;
        this.notifyLoading(this.activeRequests > 0);

        const status = error.response?.status ?? 0;
        const data = error.response?.data as any;
        const details = data?.error?.details || data?.details || data;

        // Specialized SharePoint Error Handling
        if (details?.type === 'AUTH_REQUIRED') {
          const { customerId, phaseId, siteUrl } = details;
          if (customerId) {
            const oauthUrl = this.startPhaseSharePointOAuth(customerId, phaseId, siteUrl);
            if (typeof window !== 'undefined') {
              window.location.href = oauthUrl;
            }
            return Promise.reject(error);
          }
        }

        if (details?.type === 'ACCESS_DENIED' || (status === 403 && details?.siteUrl)) {
          const { customerId, phaseId, siteUrl } = details;
          if (typeof window !== 'undefined' && customerId) {
            window.location.href = `/sharepoint/access-denied?customerId=${customerId}&phaseId=${phaseId}&siteUrl=${encodeURIComponent(siteUrl || '')}`;
            return Promise.reject(error);
          }
        }

        if (status === 401 || (data?.requiresAuth && data?.authorizeUrl)) {
          const requiresAuth = data?.requiresAuth || status === 401;
          const authorizeUrl = data?.authorizeUrl;

          if (requiresAuth && authorizeUrl) {
            // Redirect to SharePoint auth flow with returnUrl
            if (typeof window !== 'undefined') {
              try {
                const url = new URL(authorizeUrl);
                const state = JSON.parse(url.searchParams.get('state') || '{}');
                state.returnUrl = window.location.pathname + window.location.search;
                url.searchParams.set('state', JSON.stringify(state));
                window.location.href = url.toString();
              } catch (e) {
                console.error('Failed to append returnUrl to authorizeUrl', e);
                window.location.href = authorizeUrl;
              }
            }
            return Promise.reject(error);
          }

          // Redirect to welcome (login) for general 401
          if (typeof window !== 'undefined') {
            window.location.href = '/welcome';
          }
        } else if (status >= 500) {
          toast.error('Server error occurred. Please try again.');
        } else if (error.message.includes('Network Error')) {
          toast.error('Network error. Please check your connection.');
        }
        return Promise.reject(error);
      }
    );
  }

  // Loading state management
  public subscribeToLoading(listener: (isLoading: boolean) => void): () => void {
    this.loadingListeners.push(listener);
    // Emit current state immediately
    listener(this.activeRequests > 0);
    return () => {
      this.loadingListeners = this.loadingListeners.filter((l) => l !== listener);
    };
  }

  private notifyLoading(isLoading: boolean) {
    // Determine the effective loading state based on count
    // (Ensure we don't flip to false if other requests are pending)
    const effectiveIsLoading = this.activeRequests > 0;

    // We only notify if the effective state matches expectation or simply notify all
    // Simplest approach: always notify current effective state
    this.loadingListeners.forEach((listener) => listener(effectiveIsLoading));
  }

  // Generic helpers to support direct calls in components
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.put<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.delete<T>(url, config);
  }

  // Customer endpoints
  async getCustomers() {
    const response = await this.axiosInstance.get('/customers');
    return response.data;
  }

  async getCustomer(customerId: string) {
    const response = await this.axiosInstance.get(`/customers/${customerId}`);
    return response.data;
  }

  async createCustomer(customer: any) {
    const response = await this.axiosInstance.post('/customers', customer);
    return response.data;
  }

  async updateCustomer(customerId: string, customer: any) {
    const response = await this.axiosInstance.put(`/customers/${customerId}`, customer);
    return response.data;
  }

  async archiveCustomer(customerId: string) {
    const response = await this.axiosInstance.post(`/customers/${customerId}?action=archive`);
    return response.data;
  }

  async unarchiveCustomer(customerId: string) {
    const response = await this.axiosInstance.post(`/customers/${customerId}?action=unarchive`);
    return response.data;
  }

  async deleteCustomer(customerId: string) {
    const response = await this.axiosInstance.delete(`/customers/${customerId}`);
    return response.data;
  }

  async renameCustomer(customerId: string, newName: string) {
    const response = await this.axiosInstance.post(`/customers/${customerId}?action=rename`, { newName });
    return response.data;
  }

  // SharePoint integration
  async getSharePointStatus(customerId: string) {
    const res = await this.axiosInstance.get(`/sharepoint/${customerId}`);
    return res.data;
  }
  async connectSharePoint(customerId: string, payload: { siteUrl?: string; driveId?: string; tenantId?: string; appId?: string }) {
    const res = await this.axiosInstance.post(`/sharepoint/${customerId}/connect`, payload);
    return res.data;
  }

  // Phase endpoints
  async getPhases(customerId: string) {
    const response = await this.axiosInstance.get(`/customers/${customerId}/phases`);
    return response.data;
  }

  async createPhase(customerId: string, phase: any) {
    const response = await this.axiosInstance.post(`/customers/${customerId}/phases`, phase);
    return response.data;
  }

  async savePhaseEntities(customerId: string, phaseId: string, entities: any[]) {
    const response = await this.axiosInstance.put(`/customers/${customerId}/phases`, { phaseId, entities });
    return response.data;
  }

  async updatePhase(customerId: string, phaseId: string, phase: any) {
    const response = await this.axiosInstance.put(`/customers/${customerId}/phases/${phaseId}`, phase);
    return response.data;
  }

  // Dashboard endpoints
  async getDashboardStats() {
    const response = await this.axiosInstance.get('/dashboard/stats');
    return response.data;
  }

  // Credentials endpoints (SFSF - phase level)
  async saveCredentials(customerId: string, phaseId: string, credentials: any) {
    const response = await this.axiosInstance.post(`/credentials/${customerId}/${phaseId}`, credentials);
    return response.data;
  }

  async testCredentials(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.post(`/credentials/${customerId}/${phaseId}/test`);
    return response.data;
  }

  // Customer-level SharePoint credentials
  async getCustomerSharePointCredentials(customerId: string) {
    const response = await this.axiosInstance.get(`/credentials/${customerId}/sharepoint`);
    return response.data;
  }

  async saveCustomerSharePointCredentials(customerId: string, credentials: any) {
    const response = await this.axiosInstance.post(`/credentials/${customerId}/sharepoint`, credentials);
    return response.data;
  }

  async testCustomerSharePointCredentials(customerId: string, credentials?: any) {
    const response = await this.axiosInstance.post(`/credentials/${customerId}/sharepoint/test`, credentials);
    return response.data;
  }

  async getCertificate(type?: string) {
    const response = await this.axiosInstance.get('/credentials/certificate', {
      params: { type, format: 'json' }
    });
    return response.data;
  }

  async deleteCustomerSharePointCredentials(customerId: string) {
    const response = await this.axiosInstance.delete(`/credentials/${customerId}/sharepoint`);
    return response.data;
  }

  // Metadata endpoints
  async getMetadata(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/metadata/${customerId}/${phaseId}`);
    return response.data;
  }

  async discoverDynamicEntities(customerId: string, phaseId: string, entity: string) {
    const response = await this.axiosInstance.post(`/metadata/${customerId}/${phaseId}/discover`, { entity });
    return response.data;
  }

  async refreshMetadata(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.post(`/metadata/${customerId}/${phaseId}/refresh`);
    return response.data;
  }

  async refreshMetadataEntity(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.post(`/metadata/${customerId}/${phaseId}/refresh/${entityName}`);
    return response.data;
  }

  // File endpoints
  async uploadFile(customerId: string, phaseId: string, fileType: string, file: File, fileName?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (fileName) {
      formData.append('fileName', fileName);
    }

    const response = await this.axiosInstance.post(`/files/${customerId}/${phaseId}/${fileType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getFiles(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/files/${customerId}/${phaseId}`);
    return response.data;
  }

  async getPhaseEntities(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/customers/${customerId}/phases/${phaseId}/entities`);
    return response.data;
  }

  async updatePhaseEntities(customerId: string, phaseId: string, entities: any[]) {
    const response = await this.axiosInstance.put(`/customers/${customerId}/phases/${phaseId}/entities`, { entities });
    return response.data;
  }

  // Phase SharePoint
  async getPhaseSharePoint(customerId: string, phaseId: string) {
    const res = await this.axiosInstance.get(`/sharepoint/${customerId}/${phaseId}`);
    return res.data;
  }
  async connectPhaseSharePoint(customerId: string, phaseId: string, payload: { siteUrl?: string; driveId?: string; tenantId?: string; appId?: string }) {
    const res = await this.axiosInstance.post(`/sharepoint/${customerId}/${phaseId}/connect`, payload);
    return res.data;
  }
  // Start OAuth redirect (no JSON fetch; causes navigation)
  startPhaseSharePointOAuth(customerId: string, phaseId: string, siteUrl?: string, opts?: { appId?: string; tenantId?: string }) {
    const base = this.axiosInstance.defaults.baseURL?.replace(/\/$/, '') || '';
    const params = new URLSearchParams();
    if (siteUrl) params.set('siteUrl', siteUrl);
    if (opts?.appId) params.set('appId', opts.appId);
    if (opts?.tenantId) params.set('tenantId', opts.tenantId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return `${base}/sharepoint/${customerId}/${phaseId}/oauth/start${qs}`;
  }
  async copyPhaseSharePoint(customerId: string, phaseId: string, fromCustomerId: string, fromPhaseId: string) {
    const res = await this.axiosInstance.post(`/sharepoint/${customerId}/${phaseId}/copy`, { fromCustomerId, fromPhaseId });
    return res.data;
  }

  // SharePoint browsing
  async browseSharePoint(customerId: string, phaseId: string, path?: string) {
    const url = `/sharepoint/${customerId}/${phaseId}/browse${path ? `?path=${encodeURIComponent(path)}` : ''}`;
    const res = await this.axiosInstance.get(url);
    return res.data;
  }
  async inspectSharePointFile(customerId: string, phaseId: string, itemId: string) {
    const res = await this.axiosInstance.get(`/sharepoint/${customerId}/${phaseId}/inspect?itemId=${encodeURIComponent(itemId)}`);
    return res.data;
  }
  async saveSharePointSelection(customerId: string, phaseId: string, files: Array<{ id: string; name: string; path: string; size?: number; fileType: string; columns: string[] }>) {
    const res = await this.axiosInstance.post(`/sharepoint/${customerId}/${phaseId}/selection`, { files });
    return res.data;
  }
  async getSharePointSelection(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/sharepoint/${customerId}/${phaseId}/selection`);
    return response.data;
  }
  async checkSharePointSelection(customerId: string, phaseId: string) {
    const res = await this.axiosInstance.get(`/sharepoint/${customerId}/${phaseId}/check-selection`);
    return res.data;
  }

  async cacheSharePointFile(customerId: string, phaseId: string, selectionId: string) {
    const response = await this.axiosInstance.post(`/sharepoint/${customerId}/${phaseId}/copy/file`, { selectionId });
    return response.data;
  }

  async getSharePointPreview(customerId: string, phaseId: string, selectionId: string) {
    const response = await this.axiosInstance.get(`/sharepoint/${customerId}/${phaseId}/preview`, {
      params: { selectionId }
    });
    return response.data;
  }
  async searchSharePointSites(customerId: string, phaseId: string, search: string) {
    const res = await this.axiosInstance.get(`/sharepoint/${customerId}/${phaseId}/sites?search=${encodeURIComponent(search)}`);
    return res.data;
  }
  async setSharePointSite(customerId: string, phaseId: string, webUrl: string) {
    const res = await this.axiosInstance.post(`/sharepoint/${customerId}/${phaseId}/site`, { webUrl });
    return res.data;
  }

  // Mapping endpoints
  async getMappings(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}`);
    return response.data;
  }

  async getEntitiesConfig() {
    const response = await this.axiosInstance.get('/entities.json');
    return response.data;
  }

  async saveMappings(customerId: string, phaseId: string, mappings: any[]) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}`, mappings);
    return response.data;
  }

  async getEntityMappings(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/entities`);
    return response.data;
  }

  async getEntityMapping(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/entity?entityName=${encodeURIComponent(entityName)}`);
    return response.data;
  }

  async saveEntityMapping(customerId: string, phaseId: string, entityMapping: any) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/entity`, entityMapping);
    return response.data;
  }

  async validateEntityMapping(customerId: string, phaseId: string, entityName: string, mappings: any[]) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/validate-entity`, {
      entityName,
      mappings
    });
    return response.data;
  }

  async getEmployeeIdMappings(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/employee-ids`);
    return response.data;
  }

  async saveEmployeeIdMappings(customerId: string, phaseId: string, mappings: any[]) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/employee-ids`, { mappings });
    return response.data;
  }

  async getDefaultOptionMappings(customerId: string, phaseId: string, entityName: string, fieldName: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/default-options/${entityName}/${fieldName}`);
    return response.data;
  }

  async saveDefaultOptionMappings(customerId: string, phaseId: string, entityName: string, fieldName: string, payload: any) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/default-options/${entityName}/${fieldName}`, payload);
    return response.data;
  }

  async getSourceValues(customerId: string, phaseId: string, entityName: string, fieldName: string, sourceColumn?: string) {
    const params: any = {};
    if (sourceColumn) params.sourceColumn = sourceColumn;
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/source-values/${entityName}/${fieldName}`, { params });
    return response.data;
  }

  async saveValueMappings(customerId: string, phaseId: string, entityName: string, fieldName: string, valueMappings: any) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/value-mapping`, {
      entityName,
      fieldName,
      valueMappings
    });
    return response.data;
  }

  // Validation endpoints
  async validateMappings(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.post(`/validation/${customerId}/${phaseId}/mappings`);
    return response.data;
  }

  async validateData(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.post(`/validation/${customerId}/${phaseId}/data`);
    return response.data;
  }

  // Export endpoints
  async createExport(customerId: string, phaseId: string, config: any, mappings?: any[]) {
    const response = await this.axiosInstance.post(`/export/${customerId}/${phaseId}`, { ...config, mappings });
    return response.data;
  }

  async getExportResult(customerId: string, phaseId: string, exportId: string) {
    const response = await this.axiosInstance.get(`/export/${customerId}/${phaseId}/${exportId}`);
    return response.data;
  }

  // Wizard endpoints
  async getWizardStatus(customerId: string, phaseId: string) {
    const url = `/wizard/${customerId}/${phaseId}/status`
    logWizardApi('getWizardStatus', { customerId, phaseId, url })
    const response = await this.axiosInstance.get(url)
    return response.data
  }

  async validateWizardStep(
    customerId: string,
    phaseId: string,
    stepId: string
  ) {
    const url = `/wizard/${customerId}/${phaseId}/validate-step/${stepId}`
    logWizardApi('validateWizardStep', { customerId, phaseId, stepId })
    const response = await this.axiosInstance.post(url, {})
    return response.data
  }

  async completeWizardStep(
    customerId: string,
    phaseId: string,
    stepId: string,
    options?: { skip?: boolean }
  ) {
    const url = `/wizard/${customerId}/${phaseId}/complete-step/${stepId}`
    logWizardApi('completeWizardStep', {
      customerId,
      phaseId,
      stepId,
      skip: options?.skip || false,
    })
    const payload = options?.skip ? { skip: true } : {}
    const response = await this.axiosInstance.post(url, payload)
    return response.data
  }

  // Progress endpoints
  async getProgress(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/progress/${customerId}/${phaseId}`);
    return response.data;
  }

  // Reports endpoints
  async getReports(type: string, customerId?: string, phaseId?: string) {
    let url = `/reports/${type}`;
    if (customerId) url += `/${customerId}`;
    if (phaseId) url += `/${phaseId}`;

    const response = await this.axiosInstance.get(url);
    return response.data;
  }

  async getMappingPreview(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/preview`, {
      params: { entityName }
    });
    return response.data;
  }

  async generateMappingPreview(customerId: string, phaseId: string, entityName: string, mapping?: any, generatedBy?: string) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/preview`, {
      entityName,
      mapping,
      generatedBy
    });
    return response.data;
  }

  async listMappingVersions(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/entity/versions`, {
      params: { entityName }
    });
    return response.data;
  }

  async getMappingVersion(customerId: string, phaseId: string, entityName: string, versionId: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/entity/version`, {
      params: { entityName, versionId }
    });
    return response.data;
  }

  async saveMappingVersion(customerId: string, phaseId: string, version: any) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/entity/version`, version);
    return response.data;
  }

  async getEntityStatusSummary(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/entities`, {
      params: { summary: 'true' }
    });
    return response.data;
  }

  async lockEntityMapping(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.get(`/mapping/${customerId}/${phaseId}/lock`, {
      params: { entityName }
    });
    return response.data;
  }

  async unlockEntityMapping(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/unlock`, { entityName });
    return response.data;
  }

  async updateEntityMappingStatus(customerId: string, phaseId: string, entityName: string, status: string) {
    const response = await this.axiosInstance.post(`/mapping/${customerId}/${phaseId}/entity/status`, { entityName, status });
    return response.data;
  }

  // Entity Mapping V2 endpoints
  async getEntityMappingsV2(customerId: string, phaseId: string) {
    const response = await this.axiosInstance.get(`/mapping-v2/${customerId}/${phaseId}/entities`);
    return response.data;
  }

  async getEntityMappingV2(customerId: string, phaseId: string, entityName: string) {
    const response = await this.axiosInstance.get(`/mapping-v2/${customerId}/${phaseId}/entity?entityName=${encodeURIComponent(entityName)}`);
    return response.data;
  }

  async saveEntityMappingV2(customerId: string, phaseId: string, entityMapping: any) {
    const response = await this.axiosInstance.post(`/mapping-v2/${customerId}/${phaseId}/entity`, entityMapping);
    return response.data;
  }

  async getValueMappingTableV2(customerId: string, phaseId: string, entityName: string, fieldName: string) {
    const response = await this.axiosInstance.get(`/mapping-v2/${customerId}/${phaseId}/value-mappings/${entityName}/${fieldName}`);
    return response.data;
  }

  async saveValueMappingTableV2(customerId: string, phaseId: string, entityName: string, fieldName: string, mappings: any[]) {
    const response = await this.axiosInstance.post(`/mapping-v2/${customerId}/${phaseId}/value-mappings/${entityName}/${fieldName}`, mappings);
    return response.data;
  }

  async validateEntityMappingV2(customerId: string, phaseId: string, entityName: string, fieldMappings: any[]) {
    const response = await this.axiosInstance.post(`/mapping-v2/${customerId}/${phaseId}/validate`, {
      entityName,
      fieldMappings
    });
    return response.data;
  }

  async exportMappingV2(customerId: string, phaseId: string, entityName: string, format: 'json' | 'csv' = 'json') {
    const response = await this.axiosInstance.post(`/mapping-v2/${customerId}/${phaseId}/export`, {
      entityName,
      format
    });
    return response.data;
  }

  async importMappingV2(customerId: string, phaseId: string, entityName: string, data: any, format: 'json' | 'csv' = 'json') {
    const response = await this.axiosInstance.post(`/mapping-v2/${customerId}/${phaseId}/import`, {
      entityName,
      data,
      format
    });
    return response.data;
  }

  // User Profile Sync
  async syncUserProfile() {
    // This calls the backend function which checks/creates the profile blob using the auth headers
    const response = await this.axiosInstance.get('/user/me');
    return response.data;
  }

  // User Management endpoints
  async getUsers() {
    const response = await this.axiosInstance.get('/users');
    return response.data;
  }

  async inviteUser(email: string, name: string) {
    const response = await this.axiosInstance.post('/users/invite', { email, name });
    return response.data;
  }

  async updateUser(userId: string, data: { customers?: string[], isActive?: boolean }) {
    const response = await this.axiosInstance.put(`/users/${userId}`, data);
    return response.data;
  }

}

export const apiClient = new ApiClient();
export default apiClient;
